import React from 'react';
import cx from 'utils/classnames';
import { capitalizeFirstLetter } from 'utils/stringHelpers';
import SectionTitle from 'components/blocks/headers/section-title/SectionTitle';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import Slides from './Slides';
import useSlider from './useSlider';
import SliderDirectionNav from './SliderDirectionNav';
import SliderIndexNav from './SliderIndexNav';

interface SliderProps {
  imageAlignment: string;
  customCssClass: string;
  variation: string;
  background: string;
  isReversed: boolean;
  mediaWidth: string;
  title: string;
  subTitle: string;
  slides: {
    content: string;
    cta: {
      useCTA: boolean;
      buttonText: string;
      link: string;
      buttonActionType: string | boolean;
    };
    images: {
      url: string;
      alt: string;
      mimeType: string;
      width: string | number;
      height: string | number;
      sizes: {
        height: number;
        width: number;
        url: string;
      };
    };
    subTitle: string;
    title: string;
    videoEmbedId: string;
  }[];
}

function Slider(props: SliderProps): React.ReactNode {
  const {
    imageAlignment,
    customCssClass,
    variation,
    background,
    isReversed,
    mediaWidth,
    title,
    subTitle,
    slides,
  } = props;

  const { sliderState, gotoSlide, gotoNextSlide, gotoPrevSlide } = useSlider(
    slides.length
  );

  const sliderClass = cx({
    slider: true,
    [`slider--${sliderState.slideDirection}`]: true,
    [`slider--media${capitalizeFirstLetter(imageAlignment)}`]: true,
    [`slider--variation${variation ? capitalizeFirstLetter(variation) : ''}`]:
      !!variation,
    [`slider--background${
      background ? capitalizeFirstLetter(background) : ''
    }`]: !!background,
    [`slider--mediaWidth${
      mediaWidth ? capitalizeFirstLetter(mediaWidth) : ''
    }`]: !!mediaWidth,
    [`slider--isReversed`]: isReversed,
    [`slider--${customCssClass}`]: !!customCssClass,
  });

  return (
    <div className={sliderClass}>
      <div className="slider__inner">
        {title && (
          <div className="slider__title">
            <SectionTitle title={title} />
          </div>
        )}
        {subTitle && (
          <h2 className="slider__subTitle">
            <Wysiwyg content={subTitle} />
          </h2>
        )}
        {slides && slides.length > 1 && (
          <SliderDirectionNav
            handleGotoNext={gotoNextSlide}
            handleGotoPrev={gotoPrevSlide}
            isTransitioning={sliderState.isTransitioning}
          />
        )}
        <Slides
          slides={slides}
          slidePosition={sliderState.infiniteSlidePosition}
          shouldTransition={sliderState.shouldTransition}
          handleGotoNext={gotoNextSlide}
          handleGotoPrev={gotoPrevSlide}
          options={{
            imageAlignment,
            customCssClass,
            variation,
            background,
            isReversed,
            mediaWidth,
          }}
        />
        {slides && slides.length > 1 && (
          <SliderIndexNav
            currentIndex={sliderState.currentIndex}
            slides={slides}
            handleGotoSlide={gotoSlide}
          />
        )}
      </div>
    </div>
  );
}

export default Slider;
